import React, { useState, useEffect } from 'react';
import Campaign from '../components/Campaign.js';
import axios from 'axios';
import ResponsiveContainer from '../components/ResponsiveContainer.js';
import InfoMessage from '../components/InfoMessage.js';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../config/theme.js';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles((theme) => ({
  main: {},
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '14px',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  lightningIcon: {
    top: '7px',
    position: 'relative',
    filter: 'brightness(0) saturate(100%) invert(16%) sepia(97%) saturate(2586%) hue-rotate(222deg) brightness(89%) contrast(101%)',
  },
  title: {
    fontWeight: 700,
    marginBottom: 0,
    color: '#1A1A1A',
  },
  searchWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    width: '170px',
    height: '34px',
    borderRadius: '8px',
    border: '1px solid #D9DDE5',
    backgroundColor: '#F3F6FB',
    color: '#2A5B2C',
    padding: '0 10px',
    boxSizing: 'border-box',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  searchIcon: {
    color: '#6E916F',
    fontSize: '1.1rem',
    flexShrink: 0,
  },
  searchBox: {
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: '#2A5B2C',
    fontSize: '0.875rem',
    width: '100%',
    '&::placeholder': {
      color: '#6E916F',
    },
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '16px',
  },
  filterButton: {
    minWidth: '95px',
    height: '36px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
    border: '1px solid #AAB5C4',
    backgroundColor: '#FFFFFF',
    color: '#1F3D20',
    padding: '0 14px',
    cursor: 'pointer',
    fontSize: '0.95rem',
    fontWeight: 500,
  },
  selected: {
    color: '#FFFFFF',
    borderColor: '#3B7E3D',
    backgroundColor: '#3B7E3D',
  },
  campaigns: {
    minHeight: '600px',
  },
  divider: {
    border: 0,
    borderTop: '1px solid #E8EDF5',
    margin: '10px 0 12px',
  },
  info: {
    color: '#666666',
    margin: '40px',
    textAlign: 'center',
    fontSize: '110%',
    fontWeight: 600,
  },
  button: {
    color: '#3B7E3D',
    borderRadius: '8px',
    border: '2px solid #3B7E3D',
    backgroundColor: 'transparent',
    padding: '8px 20px',
    cursor: 'pointer',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'rgba(59, 126, 61, 0.08)',
    },
  },
}));

export default function CampaignsIndex({ influencer }) {
  const classes = useStyles();
  const [campaigns, setCampaigns] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(20);
  const [finalPage, setFinalPage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchButton, setSearchButton] = useState('active');
  const [searchTerm, setSearchTerm] = useState('');

  function filterCampaignsByTab(items, tab) {
    if (tab === 'soon') {
      return items.filter((campaign) => campaign.user_status === 'waiting_start');
    }

    if (tab === 'concluded') {
      return items.filter((campaign) =>
        campaign.user_status === 'finished' && campaign.drawn_ticket_number
      );
    }

    if (tab === 'closed') {
      return items.filter((campaign) =>
        ['waiting_drawn', 'sold_out'].includes(campaign.user_status)
      );
    }

    return items;
  }

  async function fetchCampaigns(page) {
    setLoading(true);
    try {
      const searchType = searchButton === 'soon'
        ? 'active'
        : ['concluded', 'closed'].includes(searchButton) ? 'past' : searchButton;
      const response = await axios.get(`/api/v1/campaigns/${influencer.username}/history.json?page=${page}&per_page=${perPage}&type=${searchType}`);
      if (response.data.length != perPage)
        setFinalPage(true);
      let items = filterCampaignsByTab(response.data || [], searchButton);
      setCampaigns(campaigns.concat(items));
    } catch (error) {
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  }

  function loadMore() {
    fetchCampaigns(currentPage + 1);
    setCurrentPage(currentPage + 1);
  }

  useEffect(() => {
    fetchCampaigns(currentPage);
  }, []);

  useEffect(() => {
    fetchCampaigns(1);
  }, [searchButton]);

  function search(term) {
    setFinalPage(false);
    setCampaigns([]);
    setCurrentPage(1);
    setSearchButton(term);
  }

  return (
    <ThemeProvider theme={theme}>
      <ResponsiveContainer>
        <div>
          <div className={classes.headerRow}>
            <Typography variant="h5" className={classes.title}>
              <img src="https://d2zdlj1ifv8u1d.cloudfront.net/images/lightning.svg" className={classes.lightningIcon} />&nbsp;
              Campanhas
            </Typography>
            <div className={classes.searchWrapper}>
              <SearchIcon className={classes.searchIcon} />
              <input
                className={classes.searchBox}
                placeholder="Buscar"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className={classes.filters}>
            <button className={`${classes.filterButton} ${searchButton == 'active' ? classes.selected : ''}`} onClick={() => search('active')}>Ativas</button>
            <button className={`${classes.filterButton} ${searchButton == 'concluded' ? classes.selected : ''}`} onClick={() => search('concluded')}>Concluídas</button>
            <button className={`${classes.filterButton} ${searchButton == 'closed' ? classes.selected : ''}`} onClick={() => search('closed')}>Fechadas</button>
            <button className={`${classes.filterButton} ${searchButton == 'soon' ? classes.selected : ''}`} onClick={() => search('soon')}>Em Breve</button>
          </div>
          <hr className={classes.divider} />
          <div className={classes.campaigns}>
            {!loading && campaigns
              .filter((campaign) => `${campaign?.title || ''} ${campaign?.subtitle || ''}`.toLowerCase().includes(searchTerm.toLowerCase()))
              .map((campaign) => (
              <Campaign key={campaign.id} campaign={campaign} />
            ))}
            {loading && <div className={classes.info}>Carregando...</div>}
            {!loading && campaigns.length == 0 && <InfoMessage>Nenhuma campanha encontrada</InfoMessage>}
          </div>
          <div>
            {!finalPage && <button disabled={loading} className={classes.button} onClick={loadMore}>Carregar mais</button>}
          </div>
        </div>
      </ResponsiveContainer>
    </ThemeProvider>
  );
};